import { decodeJwtPayload } from './jwtPayload';
import { resolveProfileRecord } from '../utils/profileDisplaySummary';
import {
  isAssetOwnerLoginType,
  isBusinessClientLogin,
  isBusinessWorkerLogin,
} from './userLoginType';

/** Roles the booking API accepts (see server message: workers + private clients only). */
export function canCreateBookingsByLoginType(loginType: string | null | undefined): boolean {
  if (!loginType) {
    return false;
  }
  return loginType === 'private client' || isBusinessWorkerLogin(loginType);
}

export function isBookingRoleDeniedApiMessage(message: string | null | undefined): boolean {
  const text = message?.trim().toLowerCase() ?? '';
  if (!text) {
    return false;
  }
  return (
    text.includes('required role') ||
    text.includes('only workers and private clients') ||
    text.includes('private clients can book')
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pickString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Offizz role ids (RegisterTypeMenu): 1 private client, 2 business client, 3 business worker, 4/5 asset owners. */
export const BOOKABLE_SERVER_ROLE_IDS = new Set([1, 3]);
export const NON_BOOKABLE_SERVER_ROLE_IDS = new Set([2, 4, 5, 6]);

export function roleIdFromJwt(accessToken: string | null | undefined): number | null {
  const token = accessToken?.trim();
  if (!token) {
    return null;
  }
  return roleIdFromRecord(decodeJwtPayload(token));
}

function jwtClaimsNonBookableRole(payload: Record<string, unknown>): boolean {
  const roleId = roleIdFromRecord(payload);
  if (roleId !== null) {
    return NON_BOOKABLE_SERVER_ROLE_IDS.has(roleId);
  }
  const rawRoles = payload.roles ?? payload.role;
  const entries = Array.isArray(rawRoles) ? rawRoles : rawRoles != null ? [rawRoles] : [];
  for (const entry of entries) {
    const text = String(entry).trim().toLowerCase();
    if (!text) {
      continue;
    }
    if (/private\s*client|privateclient/.test(text)) {
      return false;
    }
    if (/business\s*worker|businessworker|\bworker\b/.test(text)) {
      return false;
    }
    if (/business\s*client|asset\s*owner|\bowner\b/.test(text)) {
      return true;
    }
  }
  return false;
}

export type ServerBookingRoleBlock = {
  roleId: number | null;
  source: 'profile' | 'jwt';
  jwtSummary: string | null;
};

/**
 * Block before POST /bookings when the server account clearly lacks a bookable role,
 * even though the UI login type is private client / business worker.
 */
export function resolveServerBookingRoleBlock(
  loginType: string | null | undefined,
  profileRecord: Record<string, unknown> | null,
  accessToken: string | null | undefined,
): ServerBookingRoleBlock | null {
  if (!canCreateBookingsByLoginType(loginType)) {
    return null;
  }

  const profileRoleId = roleIdFromRecord(profileRecord);
  if (profileRoleId !== null && NON_BOOKABLE_SERVER_ROLE_IDS.has(profileRoleId)) {
    return {
      roleId: profileRoleId,
      source: 'profile',
      jwtSummary: jwtRoleClaimSummary(accessToken),
    };
  }

  const token = accessToken?.trim();
  if (token) {
    const payload = decodeJwtPayload(token);
    if (payload) {
      const jwtRoleId = roleIdFromRecord(payload);
      if (jwtRoleId !== null && NON_BOOKABLE_SERVER_ROLE_IDS.has(jwtRoleId)) {
        return {
          roleId: jwtRoleId,
          source: 'jwt',
          jwtSummary: jwtRoleClaimSummary(accessToken),
        };
      }
      if (jwtClaimsNonBookableRole(payload)) {
        return {
          roleId: jwtRoleId,
          source: 'jwt',
          jwtSummary: jwtRoleClaimSummary(accessToken),
        };
      }
    }
  }

  return null;
}

function roleIdFromRecord(record: Record<string, unknown> | null): number | null {
  if (!record) {
    return null;
  }
  const raw = record.role_id ?? record.roleId;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function profileRoleIdFromData(data: unknown): number | null {
  return roleIdFromRecord(resolveProfileRecord(data));
}

/** Known asset-owner role ids when paired with owner-only profile fields. */
const KNOWN_ASSET_OWNER_ROLE_IDS = new Set([4, 5, 6]);

function ownerNestedRecordHasData(value: unknown): boolean {
  if (!isPlainObject(value)) {
    return false;
  }
  return Object.values(value).some((entry) => {
    if (entry === null || entry === undefined || entry === '') {
      return false;
    }
    if (typeof entry === 'string') {
      return entry.trim().length > 0;
    }
    return true;
  });
}

/**
 * Best-effort profile hints for sessions without a bookable login type.
 * Do not use this to override `private client` / `business worker` in localStorage.
 */
export function profileRecordBlocksBooking(record: Record<string, unknown> | null): boolean {
  if (!record) {
    return false;
  }
  const roleId = roleIdFromRecord(record);
  const hasBusinessName = Boolean(pickString(record.business_name));
  if (hasBusinessName && roleId !== null && KNOWN_ASSET_OWNER_ROLE_IDS.has(roleId)) {
    return true;
  }
  if (hasBusinessName && ownerNestedRecordHasData(record.asset_owner)) {
    return true;
  }
  for (const key of ['private_asset_owner', 'business_asset_owner'] as const) {
    if (ownerNestedRecordHasData(record[key])) {
      return true;
    }
  }
  return false;
}

export function jwtRoleClaimSummary(accessToken: string | null | undefined): string | null {
  const token = accessToken?.trim();
  if (!token) {
    return null;
  }
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }
  const parts: string[] = [];
  for (const key of ['role', 'roles', 'role_id', 'roleId', 'login_type', 'loginType', 'user_type', 'userType']) {
    const value = payload[key];
    if (value === undefined || value === null || value === '') {
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      parts.push(`${key}=${String(value)}`);
      continue;
    }
    if (Array.isArray(value)) {
      parts.push(`${key}=${value.map(String).join(',')}`);
    }
  }
  return parts.length > 0 ? parts.join('; ') : null;
}

export type BookingRoleBlockReason =
  | 'asset_owner'
  | 'business_client'
  | 'profile_role'
  | 'login_type'
  | 'server_role';

export function resolveBookingRoleBlockReason(
  loginType: string | null | undefined,
  profileRecord: Record<string, unknown> | null,
): BookingRoleBlockReason | null {
  if (isAssetOwnerLoginType(loginType)) {
    return 'asset_owner';
  }
  if (isBusinessClientLogin(loginType)) {
    return 'business_client';
  }
  if (canCreateBookingsByLoginType(loginType)) {
    return null;
  }
  if (profileRecordBlocksBooking(profileRecord)) {
    return 'profile_role';
  }
  if (loginType) {
    return 'login_type';
  }
  return null;
}
